import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { DeleteEntityDialog } from "@admin-ui/components/composed/DeleteEntityDialog";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { Tab, TabList, TabPanel, Tabs } from "@admin-ui/components/primitives/application/tabs/tabs";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { Head, router, usePage } from "@inertiajs/react";
import { adminAccountPath, adminUserPath, adminUsersPath } from "@travily/routes";
import type AdminUsersShow from "@travily/types/pages/AdminUsersShow";
import { Trash01 } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import type { SharedProps } from "@/types";
import { DetailsView, ShowView } from "./show.view";

type AdminUsersShowPageProps = AdminUsersShow;

function AdminUsersShowPage({ user, accounts }: AdminUsersShowPageProps) {
  const account = useAdminLayoutAccount();
  const { current_user } = usePage<SharedProps>().props;
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isSelf = current_user?.id === user.id;
  // Anonymized users are terminal tombstones — re-deleting is a no-op the server
  // guard rejects, so hide the affordance rather than offer a dead action.
  const isAnonymized = user.lifecycle_state === "anonymized";

  return (
    <AdminLayout activeUrl={adminUsersPath()} account={account}>
      <Head title={`${user.name ?? user.email} - Admin`} />

      <AdminPageHeader
        breadcrumbs={[{ label: "Users", href: adminUsersPath() }, { label: user.name ?? user.email }]}
        title={user.name ?? user.email}
        actions={
          isSelf || isAnonymized ? undefined : (
            <Dropdown.Root>
              <Dropdown.DotsButton />
              <Dropdown.Popover className="w-min">
                <Dropdown.Menu
                  onAction={(key) => {
                    if (key === "delete") setDeleteOpen(true);
                  }}
                >
                  <Dropdown.Item id="delete" icon={Trash01} label="Delete user" />
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          )
        }
      />

      <DeleteEntityDialog
        entityLabel="user"
        entityName={user.name ?? user.email}
        deletionEffect={user.deletion_effect}
        commerceCount={user.commerce_count}
        isOpen={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={(force) => {
          router.delete(adminUserPath(user.id), { data: { force } });
        }}
      />

      <div className="mx-auto w-full max-w-container px-4 lg:px-8">
        <Tabs
          defaultSelectedKey={new URLSearchParams(usePage().url.split("?")[1]).get("tab") ?? "details"}
          onSelectionChange={(key) => {
            router.get(adminUserPath(user.id), { tab: String(key) }, { preserveState: true, preserveScroll: true });
          }}
        >
          <TabList orientation="horizontal" type="button-minimal">
            <Tab id="details" label="Details" />
            <Tab id="accounts" label="Accounts" badge={String(accounts.length)} />
          </TabList>

          <TabPanel id="details">
            <DetailsView user={user} />
          </TabPanel>

          <TabPanel id="accounts">
            <div className="pt-8">
              <ShowView user={user} accounts={accounts} adminAccountPath={adminAccountPath} />
            </div>
          </TabPanel>
        </Tabs>
      </div>
    </AdminLayout>
  );
}

AdminUsersShowPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminUsersShowPage;
